import { useParams, useNavigate } from "react-router-dom";
import { IconNames } from "@/types/v2";
import { useCardDetailQuery } from "@/hooks/queries/useCardDetailQuery";
import { useStore } from "@/stores/useStore";
import { BreadcrumbPage } from "@/components/Page/BreadcrumbPage";
import { useCardBreadcrumbs } from "@/components/Cards/CardBreadcrumb";
import { CardDetailsView } from "@/components/Cards/CardDetailsView";
import { Spinner } from "@/components/Common/Spinner";
import { Button } from "@/components/Base/Button";
import { Icon } from "@/components/Common/Icon";
import { AnalyticsLocationProvider } from "@/providers/AnalyticsLocationProvider";

const CardDetailsPage = () => {
  const { shopId, cardId } = useParams();
  const navigate = useNavigate();
  const shop = useStore((s) => s.shop);
  const employees = useStore((s) => s.employees);
  const tags = useStore((s) => s.tags);

  const {
    data: cardData,
    isLoading,
    error,
  } = useCardDetailQuery(shopId!, cardId!);

  const handleBack = () => {
    navigate(-1);
  };

  if (!shopId || !cardId) {
    return (
      <div className="flex h-full w-full items-center justify-center">
        <div className="w-full max-w-2xl text-center">
          <h1 className="mb-4 text-2xl font-bold">Error</h1>
          <p className="mb-6 text-red-600">Invalid shop or card ID.</p>
          <Button onClick={() => navigate(`/${shopId}`)}>
            <Icon name={IconNames.leftArrow} size={16} />
            Back to Dashboard
          </Button>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex h-full w-full items-center justify-center">
        <Spinner />
      </div>
    );
  }

  if (error || !cardData) {
    return (
      <div className="flex h-full w-full items-center justify-center">
        <div className="w-full max-w-2xl text-center">
          <h1 className="mb-4 text-2xl font-bold">Card Not Found</h1>
          <p className="mb-6 text-red-600">
            The requested card could not be found or you don't have permission
            to view it.
          </p>
          <div className="flex justify-center gap-2">
            <Button variant="outline" onClick={handleBack}>
              <Icon name={IconNames.leftArrow} size={16} />
              Go Back
            </Button>
            <Button onClick={() => navigate(`/${shopId}`)}>
              Go to Dashboard
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (!shop) {
    return (
      <div className="flex h-full w-full items-center justify-center">
        <Spinner />
      </div>
    );
  }

  const breadcrumbs = useCardBreadcrumbs({
    shopId,
    cardId,
    cardNumber: cardData.cardNumber,
  });

  return (
    <BreadcrumbPage breadcrumbs={breadcrumbs}>
      <AnalyticsLocationProvider
        location="Card Details Page"
        additionalData={{ cardId: cardData.id, shopId: shop.id }}
      >
        <CardDetailsView
          cardData={cardData}
          shop={shop}
          employees={employees}
          tags={tags}
          onBack={handleBack}
        />
      </AnalyticsLocationProvider>
    </BreadcrumbPage>
  );
};

export default CardDetailsPage;
